/**
 * Redux store configuration utility
 * Manages the creation and configuration of the Redux store, with a
 * hand-rolled localStorage persistence layer (see localStoragePersistence.js
 * for why this replaced redux-persist).
 */

import { combineReducers } from 'redux';
import { configureStore, type Middleware } from '@reduxjs/toolkit';
import logger from 'redux-logger';

import analyticsTracker from '../redux-middlewares/analytics.middleware';
import crashReporter from '../redux-middlewares/crash.middleware';
import { loadPersistedState, persistState } from './localStoragePersistence';

// Import reducers
import authReducer from '../reducers/auth.reducer';
import portfoliosListReducer from '../reducers/portfoliosList.reducer';
import portfoliosReducer from '../reducers/portfolios.reducer';
import accountsReducer from '../reducers/accounts.reducer';
import adminReducer from '../reducers/admin.reducer';
import settingsReducer from '../reducers/settings.reducer';
import ledgersListReducer from '../reducers/ledgersList.reducer';
import ledgersReducer from '../reducers/ledgers.reducer';

// Combined Reducer
const rootReducer = combineReducers({
  auth: authReducer,
  portfoliosList: portfoliosListReducer,
  portfolios: portfoliosReducer,
  accounts: accountsReducer,
  admin: adminReducer,
  settings: settingsReducer,
  ledgersList: ledgersListReducer,
  ledgers: ledgersReducer,
});

export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof configureStoreWithPersistor>['store'];
export type AppDispatch = AppStore['dispatch'];

/**
 * Configure the Redux store with middleware and initial state
 * @returns {Object} The configured store
 */
export default function configureStoreWithPersistor() {
  // Set up middlewares based on environment
  const middlewares: Middleware[] = [analyticsTracker, crashReporter];

  // Add logger in development
  if (process.env.APP_ENV !== 'production') {
    middlewares.push(logger as unknown as Middleware);
  }

  // Synchronous rehydration (localStorage reads are synchronous, unlike the
  // AsyncStorage/IndexedDB backends redux-persist's REHYDRATE action exists
  // for): merge whatever was persisted straight into preloadedState. Passing
  // a defined persisted value through each reducer, rather than assigning it
  // directly, lets the reducer itself fall back to its own default state
  // when nothing was persisted for that slice (a reducer only applies its
  // `= initialState` default parameter when called with `undefined`).
  const persisted = loadPersistedState();
  // Persisted slices are `unknown` until each reducer validates/defaults them.
  const preloadedState = {
    auth: authReducer(persisted.auth as never, { type: 'INIT' }),
    portfoliosList: portfoliosListReducer(persisted.portfoliosList as never, {
      type: 'INIT',
    }),
    portfolios: portfoliosReducer(persisted.portfolios as never, {
      type: 'INIT',
    }),
    settings: settingsReducer(persisted.settings as never, { type: 'INIT' }),
    ledgersList: ledgersListReducer(persisted.ledgersList as never, {
      type: 'INIT',
    }),
    ledgers: ledgersReducer(persisted.ledgers as never, { type: 'INIT' }),
  };

  // Create store with Redux Toolkit's configureStore
  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) => {
      return getDefaultMiddleware({
        serializableCheck: {
          // Ignore these action types for serializability check
          ignoredActions: [
            'AUTH_LOGIN_WITH_FIREBASE_USER',
            'AUTH_LOGIN_WITH_FIREBASE_PROVIDER',
          ],
          // Ignore these paths in the state for serializability check
          ignoredPaths: ['persistance', 'auth.user'],
        },
      }).concat(middlewares);
    },
    preloadedState,
    devTools: process.env.APP_ENV !== 'production',
  });

  persistState(store);

  return { store };
}
